import { supabase } from './supabaseClient';

export const CRM_SCHEMA_MIGRATION_HINT =
  'CRM sign-up needs a one-time Supabase update. Open Supabase → SQL Editor, run supabase-crm-signup-migration.sql from this project root, then sign up again.';

export const CRM_SETTER_LEADS_MIGRATION_HINT =
  'Lead assignment needs a one-time Supabase update. Open Supabase → SQL Editor, run supabase-crm-setter-leads-migration.sql from this project root, then refresh.';

export const CRM_SETTER_CLOSER_LINKS_MIGRATION_HINT =
  'Setter → closer connections need a one-time Supabase update. Open Supabase → SQL Editor, run supabase-crm-setter-closer-links-migration.sql from this project root, then refresh.';

export const CRM_MEETINGS_MIGRATION_HINT =
  'The meetings calendar needs a one-time Supabase update. Open Supabase → SQL Editor, run supabase-crm-setter-closer-links-migration.sql from this project root, then refresh.';

export const CRM_COLORS_MIGRATION_HINT =
  'Employee colours need a one-time Supabase update. Open Supabase → SQL Editor, run supabase-crm-setter-closer-links-migration.sql from this project root, then refresh.';

export const CRM_SCRIPT_PRESETS_MIGRATION_HINT =
  'Editable script presets need a one-time Supabase update. Open Supabase → SQL Editor, run supabase-crm-setter-closer-links-migration.sql from this project root, then refresh.';

export const CRM_RECRUITING_MIGRATION_HINT =
  'The recruiting page needs a one-time Supabase update. Open Supabase → SQL Editor, run supabase-crm-setter-closer-links-migration.sql from this project root, then refresh.';

let capabilitiesPromise = null;

function looksLikeMissingTable(error) {
  if (!error) return false;
  const code = String(error.code || '');
  if (code === 'PGRST205' || code === '42P01') return true;
  const msg = String(error.message || error.details || error.hint || '').toLowerCase();
  if (msg.includes('schema cache') && msg.includes('could not find')) return true;
  return msg.includes('relation') && msg.includes('does not exist');
}

function looksLikeMissingColumn(error) {
  if (!error) return false;
  const code = String(error.code || '');
  if (code === 'PGRST204' || code === '42703') return true;
  const msg = String(error.message || error.details || error.hint || '').toLowerCase();
  if (msg.includes('schema cache') && msg.includes('could not find')) return true;
  if (msg.includes('column') && msg.includes('does not exist')) return true;
  return false;
}

/** Call after applying supabase-crm-signup-migration.sql so probes re-run. */
export function resetCrmDbCapabilities() {
  capabilitiesPromise = null;
}

/**
 * Probes PostgREST for CRM-related columns (cached for the session).
 */
export async function getCrmDbCapabilities() {
  if (!capabilitiesPromise) {
    capabilitiesPromise = (async () => {
      const caps = {
        profilesAccountSetupComplete: true,
        profilesCrmAccountType: true,
        profilesJoinCode: true,
        teamMembersCrmRole: true,
        leadsAssignedSetterId: true,
        setterCloserLinks: true,
        crmMeetings: true,
        teamMembersCrmColor: true,
        crmScriptPresets: true,
        crmRecruiting: true,
        recruitingNotifyEmail: true,
      };

      const { error: eSetup } = await supabase.from('profiles').select('account_setup_complete').limit(0);
      if (looksLikeMissingColumn(eSetup)) caps.profilesAccountSetupComplete = false;

      const { error: eCrmType } = await supabase.from('profiles').select('crm_account_type').limit(0);
      if (looksLikeMissingColumn(eCrmType)) caps.profilesCrmAccountType = false;

      const { error: eJoin } = await supabase.from('profiles').select('join_code').limit(0);
      if (looksLikeMissingColumn(eJoin)) caps.profilesJoinCode = false;

      const { error: eRole } = await supabase.from('team_members').select('crm_role').limit(0);
      if (looksLikeMissingColumn(eRole)) caps.teamMembersCrmRole = false;

      const { error: eAssign } = await supabase.from('leads').select('assigned_setter_id').limit(0);
      if (looksLikeMissingColumn(eAssign)) caps.leadsAssignedSetterId = false;

      const { error: eLinks } = await supabase.from('crm_setter_closer_links').select('id').limit(0);
      if (looksLikeMissingTable(eLinks)) caps.setterCloserLinks = false;

      const { error: eMeetings } = await supabase.from('crm_meetings').select('id').limit(0);
      if (looksLikeMissingTable(eMeetings)) caps.crmMeetings = false;

      const { error: eColor } = await supabase.from('team_members').select('crm_color').limit(0);
      if (looksLikeMissingColumn(eColor)) caps.teamMembersCrmColor = false;

      const { error: eScripts } = await supabase.from('crm_script_presets').select('id').limit(0);
      if (looksLikeMissingTable(eScripts)) caps.crmScriptPresets = false;

      const { error: eRecruit } = await supabase.from('crm_recruiting_settings').select('owner_id').limit(0);
      if (looksLikeMissingTable(eRecruit)) caps.crmRecruiting = false;

      const { error: eNotify } = await supabase.from('crm_recruiting_settings').select('notify_email').limit(0);
      if (looksLikeMissingColumn(eNotify) || looksLikeMissingTable(eNotify)) caps.recruitingNotifyEmail = false;

      return caps;
    })();
  }
  return capabilitiesPromise;
}

export function assertCrmSignupSchemaReady(caps) {
  if (!caps?.teamMembersCrmRole || !caps?.profilesJoinCode) {
    throw new Error(CRM_SCHEMA_MIGRATION_HINT);
  }
}

export function formatSupabaseError(error) {
  if (!error) return 'Unknown error';
  if (typeof error === 'string') return error;
  const msg = error.message || error.details || '';
  if (/crm_recruiting_settings|crm_applications|crm_submit_application|crm_recruiting_public/i.test(msg)) {
    return CRM_RECRUITING_MIGRATION_HINT;
  }
  if (/crm_script_presets/i.test(msg)) {
    return CRM_SCRIPT_PRESETS_MIGRATION_HINT;
  }
  if (/crm_color/i.test(msg)) {
    return CRM_COLORS_MIGRATION_HINT;
  }
  if (/crm_meetings/i.test(msg)) {
    return CRM_MEETINGS_MIGRATION_HINT;
  }
  if (/crm_setter_closer_links/i.test(msg)) {
    return CRM_SETTER_CLOSER_LINKS_MIGRATION_HINT;
  }
  if (looksLikeMissingColumn(error) || /assigned_setter_id/i.test(msg)) {
    return CRM_SETTER_LEADS_MIGRATION_HINT;
  }
  if (looksLikeMissingColumn(error) || /crm_role|join_code|account_setup_complete|crm_account_type/i.test(msg)) {
    return CRM_SCHEMA_MIGRATION_HINT;
  }
  return msg || CRM_SCHEMA_MIGRATION_HINT;
}
